import React from 'react';
import './NdaTermsForm.scss';
import { useStateSelector } from '../../../../store/selectors';
import { NdaTerms } from '../../../../slices/marketplace/timeline/models/nda-term-model';
import { TimelineParty } from '../../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import {
    DataNdaTermsType,
    DataNdaTermsTypeList,
} from '../../../../slices/marketplace/timeline/models/enums/data-nda-term-type';
import InfoTooltip from '../../../../components/info-tooltip/info-tooltip';
import {
    namesBlindedTermTooltipText,
    namesVisibleTermTooltipText,
    nonDisclosurePeriodTooltipText,
    nonSoliciationPeriodTooltipText,
} from '../../../../utils/constants';

interface NdaTermsFormProps {
    setCurrentFormState: React.Dispatch<React.SetStateAction<NdaTerms>>;
    isSellerReadOnly: boolean;
    isBuyerReadOnly: boolean;
    isSummaryView: boolean;
    allTermsSelections: NdaTerms[];
    tooltipKey: string;
    currentFormState: NdaTerms;
    lastSellerState?: NdaTerms;
}

export const NdaTermsForm = (props: NdaTermsFormProps) => {
    const isMarketplaceBuyer = useStateSelector(
        (s) => s.userSummary?.summary?.isMarketplaceBuyer
    );

    const getCheckboxColorClass = (
        itemId: DataNdaTermsType,
        category: keyof NdaTerms
    ): string => {
        const sellerCurrentTerm = props.currentFormState[category];
        if (sellerCurrentTerm === null || sellerCurrentTerm === undefined) return '';
        if (itemId === sellerCurrentTerm) return 'green';
        if (Number(itemId) < Number(sellerCurrentTerm)) return 'red';
        if (Number(itemId) > Number(sellerCurrentTerm)) return 'faded-black';
        return '';
    };

    const handleSingleCheckboxChange = (
        category: keyof NdaTerms,
        itemId: DataNdaTermsType
    ) => {
        props.setCurrentFormState((prev) => {
            const alreadySelected = prev[category] === itemId;
            return {
                ...prev,
                [category]: alreadySelected ? null : itemId,
            };
        });
    };

    const getBuyerSelectionCount = (
        category: keyof NdaTerms,
        itemId: DataNdaTermsType
    ) =>
        props.allTermsSelections.filter(
            (x) => x.party === TimelineParty.Buyer && x[category] === itemId
        ).length;

    const renderSingleCheckbox = (
        category: keyof NdaTerms,
        itemId: DataNdaTermsType
    ) => {
        const buyerSelection = props.allTermsSelections.filter(
            (x) => x.party === TimelineParty.Buyer && x[category] === itemId
        );

        const buyerSelectionInAgreemnt = buyerSelection.filter(
            (x) => !!x.agreedTermsToDocumentList?.length
        );
        const agreedSellerTermIds = buyerSelectionInAgreemnt.map((x) =>
            x.agreedTermsToDocumentList?.find((f) => f)
        );

        let sellerSelection = [];
        if (props.isSummaryView) {
            sellerSelection = props.allTermsSelections.filter(
                (x) =>
                    x.party === TimelineParty.Seller &&
                    x[category] === itemId &&
                    (x.id === props.currentFormState.id ||
                        agreedSellerTermIds.some(
                            (s) => s.agreedSellerNdaTermsId === x.id
                        ))
            );
        } else {
            sellerSelection = props.allTermsSelections.filter(
                (x) =>
                    x.party === TimelineParty.Seller && x[category] === itemId
            );
        }

        const lastCatgoryVal = props.lastSellerState
            ? Number(props.lastSellerState[category])
            : null;

        const currCatVal = Number(itemId);

        const isBetterOption = currCatVal <= lastCatgoryVal;

        const canBeSelectedBySeller = !props.lastSellerState || isBetterOption;

        const sellerSelected = sellerSelection.length > 0;
        const buyerSelected = buyerSelection.length > 0;
        const colorClass = getCheckboxColorClass(itemId, category);
        const isDisabled = isMarketplaceBuyer
            ? props.isBuyerReadOnly
            : props.isSellerReadOnly || !canBeSelectedBySeller;

        const buyerSelectionCount = props.isSummaryView
            ? getBuyerSelectionCount(category, itemId)
            : 0;

        const isChecked = props.isSummaryView
            ? props.currentFormState[category] === itemId
            : sellerSelected || buyerSelected;

        const isNumberBeforeChekboxVisible =
            props.isSummaryView && (buyerSelectionCount > 0 || isChecked);

        return (
            <div className="checkbox-wrapper">
                <div className="input-wrapper">
                    <div className="input-section">
                        <input
                            type="checkbox"
                            checked={isChecked}
                            disabled={isDisabled}
                            className={`custom-checkbox ${
                                props.isSummaryView ? '' : colorClass
                            } ${isDisabled ? 'disabled' : 'active'}`}
                            data-checkmark-content={buyerSelectionCount ?? ''}
                            onChange={() =>
                                handleSingleCheckboxChange(category, itemId)
                            }
                        />
                    </div>
                    {isNumberBeforeChekboxVisible && (
                        <span className={`checkbox-count ${colorClass}`}>
                            {buyerSelectionCount}
                        </span>
                    )}
                </div>
            </div>
        );
    };

    const renderNdaTermsForm = () => {
        return (
            <div className="nda-terms-form-component">
                <table className="table">
                    <thead>
                        <tr>
                            <th
                                rowSpan={3}
                                className="nda-terms-period-column"></th>
                            <th
                                rowSpan={2}
                                className="non-disclosure-period-column">
                                <div className="nda-title">
                                    <label>
                                        {'Non-Disclosure Period'}
                                        <InfoTooltip
                                            text={
                                                nonDisclosurePeriodTooltipText
                                            }
                                            idText={`${props.tooltipKey}-non-disclosure-period-tooltip`}
                                        />
                                    </label>
                                </div>
                            </th>
                            <th colSpan={4}>
                                <div className="nda-title">
                                    <label>
                                        {'Non-Solicitation Period'}
                                        <InfoTooltip
                                            text={
                                                nonSoliciationPeriodTooltipText
                                            }
                                            idText={`${props.tooltipKey}-non-solicitation-period-tooltip`}
                                        />
                                    </label>
                                </div>
                            </th>
                        </tr>
                        <tr>
                            <th colSpan={2} className="text-center">
                                <label>DVMs / Management</label>
                            </th>
                            <th colSpan={2} className="text-center">
                                <label>Staff</label>
                            </th>
                        </tr>
                        <tr>
                            <th></th>
                            <th className="name-visibile">
                                <label>
                                    {'Names Visible'}
                                    <InfoTooltip
                                        text={namesVisibleTermTooltipText}
                                        idText={`${props.tooltipKey}-dvms-names-visible-tooltip`}
                                    />
                                </label>
                            </th>
                            <th className="name-blinded">
                                <label>
                                    {'Names Blinded'}
                                    <InfoTooltip
                                        text={namesBlindedTermTooltipText}
                                        idText={`${props.tooltipKey}-dvms-names-blinded-tooltip`}
                                    />
                                </label>
                            </th>
                            <th className="name-visibile">
                                <label>
                                    {'Names Visible'}
                                    <InfoTooltip
                                        text={namesVisibleTermTooltipText}
                                        idText={`${props.tooltipKey}-staff-names-visible-tooltip`}
                                    />
                                </label>
                            </th>
                            <th className="name-blinded">
                                <label>
                                    {'Names Blinded'}
                                    <InfoTooltip
                                        text={namesBlindedTermTooltipText}
                                        idText={`${props.tooltipKey}-staff-names-blinded-tooltip`}
                                    />
                                </label>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {DataNdaTermsTypeList.map((item) => (
                            <tr key={item.key} className="checkbox-row">
                                <td>
                                    <label
                                        className="flex-start"
                                        style={{ whiteSpace: 'nowrap' }}>
                                        {item.label}
                                    </label>
                                </td>
                                <td>
                                    {item.id !== DataNdaTermsType.NA &&
                                        renderSingleCheckbox(
                                            'nonDisclosurePeriod',
                                            item.id
                                        )}
                                </td>
                                <td className="name-visibile">
                                    {renderSingleCheckbox(
                                        'nonSolicitationDvmNamesVisible',
                                        item.id
                                    )}
                                </td>
                                <td className="name-blinded">
                                    {renderSingleCheckbox(
                                        'nonSolicitationDvmNamesBlinded',
                                        item.id
                                    )}
                                </td>
                                <td className="name-visibile">
                                    {renderSingleCheckbox(
                                        'nonSolicitationStaffNamesVisible',
                                        item.id
                                    )}
                                </td>
                                <td className="name-blinded">
                                    {renderSingleCheckbox(
                                        'nonSolicitationStaffNamesBlinded',
                                        item.id
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    return <div className="nda-terms-form">{renderNdaTermsForm()}</div>;
};
